import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../contexts/LanguageContext';

const Services = () => {
    const { t } = useTranslation();

    const features = [
        {
            id: 1,
            title: t('features.smartDashboard.title', 'Smart Dashboard'),
            description: t('features.smartDashboard.description', 'Visualize your spending, track active free trials, and get insights into your subscription habits.'),
            icon: (
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
            ),
            color: "blue",
            tag: t('features.freeTag', 'Free')
        },
        {
            id: 2,
            title: t('features.priceRadar.title', 'Price Radar'),
            description: t('features.priceRadar.description', 'Automatic price change detection for your subscriptions. Never miss a price hike again.'),
            icon: (
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
            ),
            color: "green",
            tag: t('features.freeTag', 'Free')
        },
        {
            id: 3,
            title: t('features.smartAlerts.title', 'Smart Alerts'),
            description: t('features.smartAlerts.description', 'Get notified about promotions and special offers on your favorite subscriptions.'),
            icon: (
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-5 5v-5zM4.93 4.93l9.07 9.07-9.07 9.07L4.93 4.93z" />
                </svg>
            ),
            color: "purple",
            tag: t('features.freeTag', 'Free')
        },
        {
            id: 4,
            title: t('features.marketplace.title', 'Marketplace'),
            description: t('features.marketplace.description', 'Compare prices across different merchants and find the best deals for your subscriptions.'),
            icon: (
                <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            ),
            color: "orange",
            tag: t('features.freeTag', 'Free')
        },
        {
            id: 5,
            title: t('features.sharingSpace.title', 'Sharing Space'),
            description: t('features.sharingSpace.description', 'Split subscription costs with friends and family. Automatic top-ups and easy payment management.'),
            icon: (
                <svg className="w-8 h-8 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
            ),
            color: "pink",
            tag: t('features.freeTag', 'Free')
        },
        {
            id: 6,
            title: t('features.advancedAnalytics.title', 'Advanced Analytics'),
            description: t('features.advancedAnalytics.description', 'Premium annual reports and detailed statistics to optimize your subscription portfolio.'),
            icon: (
                <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                </svg>
            ),
            color: "indigo",
            tag: t('features.premiumTag', 'Premium')
        },
        {
            id: 7,
            title: t('features.crossPlatform.title', 'Cross-Platform'),
            description: t('features.crossPlatform.description', 'Available on iOS, Android, and Web. Your subscriptions, always at your fingertips.'),
            icon: (
                <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
            ),
            color: "teal",
            tag: t('features.freeTag', 'Free')
        },
        {
            id: 8,
            title: t('features.easyManagement.title', 'Easy Management'),
            description: t('features.easyManagement.description', 'Activate, cancel, or pause subscriptions with one click. Access cancellation policies instantly.'),
            icon: (
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
            ),
            color: "red",
            tag: t('features.freeTag', 'Free')
        }
    ];

    const getTagClasses = (tag) => {
        if (tag === t('features.premiumTag', 'Premium')) {
            return "bg-blue-100 text-blue-800";
        }
        return "bg-green-100 text-green-800";
    };

    const getBgColor = (color) => {
        const colors = {
            blue: "bg-blue-100",
            green: "bg-green-100",
            purple: "bg-purple-100",
            orange: "bg-orange-100",
            pink: "bg-pink-100",
            indigo: "bg-indigo-100",
            teal: "bg-teal-100",
            red: "bg-red-100"
        };
        return colors[color] || "bg-blue-100";
    };

    return (
        <div id="features" className="bg-gray-50 py-16">
            {/* Features Section */}
            <section>
                <div className="my-8 py-4">
                    <h2 className="my-2 text-center text-4xl text-blue-900 uppercase font-bold">
                        {t('features.title', 'Powerful Features')}
                    </h2>
                    <div className='flex justify-center'>
                        <div className='w-24 border-b-4 border-orange-600'></div>
                    </div>
                    <p className="mt-6 mx-12 text-center text-xl lg:text-2xl font-semibold text-gray-600">
                        {t('features.subtitle', 'Everything you need to master your subscription ecosystem')}
                    </p>
                </div>

                <div className="px-4 sm:px-12">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                        {features.map((feature) => (
                            <div 
                                key={feature.id}
                                className="bg-white overflow-hidden text-gray-700 hover:shadow-2xl rounded-2xl shadow-lg p-6 group border border-gray-100"
                            >
                                <div className="text-justify">
                                    <div className={`${getBgColor(feature.color)} w-16 h-16 rounded-2xl flex items-center justify-center mb-4`}>
                                        {feature.icon}
                                    </div>
                                    <h2 className="font-bold my-4 text-xl text-gray-900">{feature.title}</h2>
                                    <p className="text-gray-600 font-medium">
                                        {feature.description}
                                    </p>
                                    <span className={`inline-block mt-3 px-3 py-1 ${getTagClasses(feature.tag)} text-sm font-semibold rounded-full`}>
                                        {feature.tag}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Highlight Section */}
            <section className="mt-16">
                <div className="m-auto max-w-4xl p-2 md:p-12">
                    <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-3xl p-8 text-center text-white shadow-2xl">
                        <h3 className="text-3xl font-bold mb-4">
                            {t('services.startSaving', 'Start Saving Today')}
                        </h3>
                        <p className="text-xl mb-6 text-blue-100">
                            {t('services.startSavingDescription', 'Most features are completely free. Upgrade to Premium for unlimited access and advanced analytics.')}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <Link 
                                to="/signup" 
                                className="bg-white text-blue-900 hover:bg-blue-50 px-8 py-4 text-lg font-semibold rounded-2xl shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
                            >
                                {t('intro.getStarted', 'Get Started Free')}
                            </Link>
                            <Link 
                                to="/pricing" 
                                className="border-2 border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 text-lg font-semibold rounded-2xl transition-all duration-300 transform hover:-translate-y-1"
                            >
                                {t('services.explorePremium', 'Explore Premium')}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Services;